import sha256 from 'crypto-js/sha256';
import { AppDispatch } from '../store/store';
import { iTrack } from '../types/ITrack';
import { iLocalFileAPIResponse } from '../types/ILocalFile';
import {
  getAllSavedFilePaths,
  getTrackMatches,
  saveSelectedTrackFilePaths,
  setSelectedFilePaths,
  setTrackMatches,
} from '../store/slices/track-matcher.slice';
import { generateTrackID } from './generateTrackID';
import isElectron from './isElectron';
import { isPerfectLocalMatch } from './isPerfectLocalMatch';
import { normalizePath } from './normalizePath';
import { toDurationMs } from './toDurationMs';

export interface FilePathMap {
  [trackId: string]: string[];
}

export const getPossibleTrackIds = (track: iTrack): string[] =>
  [
    track.ID,
    track._id,
    track.SpotifyID,
    generateTrackID(track),
    sha256(
      JSON.stringify({
        artist: track.Artist?.trim() ?? '',
        title: track.Title?.trim() ?? '',
      }),
    ).toString(),
  ].filter(Boolean) as string[];

export interface ResolveExportTrackPathMapOptions {
  crateId?: string;
  trackInfo: iTrack[];
  inMemoryPaths: FilePathMap;
  allTrackMatches?: Record<string, iLocalFileAPIResponse[] | undefined>;
  dispatch: AppDispatch;
}

/**
 * Resolve matched local file paths for export by merging in-memory selections,
 * saved DB paths, and on-demand background matching for unresolved tracks.
 */
export async function resolveExportTrackPathMap(
  options: ResolveExportTrackPathMapOptions,
): Promise<FilePathMap> {
  const { crateId, trackInfo, inMemoryPaths, allTrackMatches, dispatch } =
    options;
  const merged: FilePathMap = { ...inMemoryPaths };

  if (!crateId || !isElectron()) {
    return buildFinalPathMap(trackInfo, merged, {}, allTrackMatches);
  }

  try {
    const trackIds = Array.from(
      new Set(
        trackInfo.flatMap((track) => getPossibleTrackIds(track)).filter(Boolean),
      ),
    );
    if (trackIds.length === 0) {
      return buildFinalPathMap(trackInfo, merged, {}, allTrackMatches);
    }

    const savedMap = await dispatch(
      getAllSavedFilePaths({ crateId, trackIds }),
    ).unwrap();

    const unresolvedTracks = trackInfo.filter((track) => {
      const possibleIds = getPossibleTrackIds(track);
      const hasInMemory = possibleIds.some(
        (id) => merged[id] && merged[id].length > 0,
      );
      const hasDb = possibleIds.some(
        (id) => savedMap[id] && savedMap[id].length > 0,
      );
      return !hasInMemory && !hasDb;
    });

    if (unresolvedTracks.length > 0) {
      const BATCH_SIZE = 5;
      for (let i = 0; i < unresolvedTracks.length; i += BATCH_SIZE) {
        const batch = unresolvedTracks.slice(i, i + BATCH_SIZE);
        await Promise.all(
          batch.map(async (track) => {
            try {
              const trackId = generateTrackID(track);
              const response = await dispatch(
                getTrackMatches({
                  id: trackId,
                  artist: track.Artist ?? '',
                  title: track.Title ?? '',
                  count: 10,
                  bpm: track.BPM,
                  durationMs: toDurationMs(track.duration),
                }),
              ).unwrap();

              if (!response || response.length === 0) {
                return;
              }

              dispatch(setTrackMatches({ trackId, files: response }));

              let matchedPath: string | undefined;
              if (track.PreviewURL) {
                const normalizedPreviewURL = normalizePath(track.PreviewURL);
                const matchingTrack = response.find(
                  (m) => normalizePath(m.filePath) === normalizedPreviewURL,
                );
                if (matchingTrack) {
                  matchedPath = matchingTrack.filePath;
                }
              }

              if (!matchedPath) {
                const perfectMatch = response.find((m) =>
                  isPerfectLocalMatch(m.match_score),
                );
                if (perfectMatch) {
                  matchedPath = perfectMatch.filePath;
                }
              }

              if (!matchedPath) {
                return;
              }

              const normalizedPath = normalizePath(matchedPath);
              dispatch(
                setSelectedFilePaths({
                  trackId,
                  filePaths: [normalizedPath],
                }),
              );
              await dispatch(
                saveSelectedTrackFilePaths({
                  crateId,
                  trackId,
                  filePaths: [normalizedPath],
                }),
              ).unwrap();

              getPossibleTrackIds(track).forEach((id) => {
                merged[id] = [normalizedPath];
                savedMap[id] = [normalizedPath];
              });
            } catch (error) {
              console.warn(
                `[resolveExportTrackPathMap] Background match failed for ${track.Artist} - ${track.Title}:`,
                error,
              );
            }
          }),
        );
      }
    }

    return buildFinalPathMap(trackInfo, merged, savedMap, allTrackMatches);
  } catch (error) {
    console.error('[resolveExportTrackPathMap] Error:', error);
    return merged;
  }
}

function buildFinalPathMap(
  trackInfo: iTrack[],
  merged: FilePathMap,
  savedMap: FilePathMap,
  allTrackMatches?: Record<string, iLocalFileAPIResponse[] | undefined>,
): FilePathMap {
  const finalMap: FilePathMap = {};

  trackInfo.forEach((track) => {
    const possibleIds = getPossibleTrackIds(track);
    let paths: string[] | undefined;

    const trackId = generateTrackID(track);
    const uiMatches = allTrackMatches?.[trackId];
    if (uiMatches && uiMatches.length > 0) {
      const checkedUiPaths = uiMatches
        .filter((m) => m.isSelected && m.filePath)
        .map((m) => normalizePath(m.filePath));
      if (checkedUiPaths.length > 0) {
        paths = checkedUiPaths;
      }
    }

    if (!paths) {
      const inMemoryId = possibleIds.find(
        (id) => merged[id] && merged[id].length > 0,
      );
      if (inMemoryId !== undefined) {
        paths = merged[inMemoryId];
      }
    }

    if (!paths) {
      const dbId = possibleIds.find(
        (id) => savedMap[id] && savedMap[id].length > 0,
      );
      if (dbId !== undefined) {
        paths = savedMap[dbId];
      }
    }

    possibleIds.forEach((id) => {
      finalMap[id] = paths || [];
    });
  });

  return finalMap;
}
